// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Heading, { HeadingPresentationProps } from "@store/Library/Components/Toolkit/Heading";
// import LinkBack, { LinkBackPresentationProps, LinkBackProps } from "@store/Library/Components/Toolkit/LinkBack"; // Store Custom Code
import { ImgProps } from "@store/MobiusOverrides/Img/Img";
import { LinkProps } from "@store/MobiusOverrides/Link/Link";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import useZoneIsActive from "@toolkit/Framework/Common/Hooks/useZoneIsActive";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";
// import { pxToRemArray } from "@toolkit/Framework/Common/Utilities/unitHelper"; // Store Custom Code
import { spacingStyles } from "@toolkit/Themes/common/mixins";
import React, { FC, PropsWithChildren, ReactNode } from "react";
import { css } from "styled-components";

/* Store Custom Code - Start */
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import LinkBack, { LinkBackPresentationProps, LinkBackProps } from "@store/Library/Components/Toolkit/LinkBack";
import { pxToRem, pxToRemArray } from "@toolkit/Framework/Common/Utilities/unitHelper";
import ProgressStepper, {
    ProgressStepperPresentationProps,
    ProgressStepperProps,
} from "@store/Library/Components/ProgressStepper";
import { typographyTitleStyles } from "@store/Themes/common/mixins/typography";
/* Store Custom Code - End */

const zoneName = "Message";

const wrapperStyles = () => css`
    ${x =>
        spacingStyles(
            x.theme.design.typography.spacing.small,
            x.theme.design.typography.spacing.small,
        )} /* Store Custom Code */

    && + .message {
        margin-top: 0;
    }
`;

/* Store Custom Code - Start */
export interface OrderHeaderPresentationProps extends ExpandStyles<OrderHeaderStyles> {}
/* Store Custom Code - End */

// Store Custom Code - next line - OrderHeaderPresentationProps
export interface OrderHeaderProps extends OrderHeaderPresentationProps {
    contentId?: string;
    title: string;
    message?: string | ReactNode;
    linkBack?: LinkBackProps["pageLink"];
    linkBackOnClick?: LinkBackProps["onClick"];
    image?: Pick<ImgProps, "src" | "altText">;
    link?: Pick<LinkProps, "href" | "onClick" | "target">;
    // Store Custom Code
    pageIdSegment?: string;
    progressSteps?: ProgressStepperProps["steps"];
    progressActiveStep?: ProgressStepperProps["activeStep"];
    isV2?: boolean;
    titleIcon?: Pick<IconPresentationProps, "src" | "color" | "size">;
}

export interface OrderHeaderStyles {
    wrapper?: InjectableCss;
    heading?: HeadingPresentationProps;
    messageWrapper?: InjectableCss;
    zoneWrapper?: InjectableCss;
    linkBack?: LinkBackPresentationProps;
    // Store Custom Code
    progressStepper?: ProgressStepperPresentationProps;
    titleWrapper?: InjectableCss;
    titleIcon?: IconPresentationProps;
}

export const orderHeaderStyles: OrderHeaderStyles = {
    wrapper: {
        css: css`
            ${x => breakpointValues(x.theme.design.layout.spacing.narrowContent, "margin-bottom")}

            &:last-child {
                margin-bottom: 0;
            }
        `,
    },
    heading: {
        variant: "h1",
        extendedStyles: {
            title: {
                css: css`
                    margin: 0;
                    border-bottom: ${pxToRem(3) /* Store Custom Code */} solid ${getColor("common.border")};
                    ${breakpointValues(pxToRemArray([16, null, 20, null, 24]), "padding-bottom")}
                `,
            },
        },
    },
    messageWrapper: {
        css: wrapperStyles(),
    },
    zoneWrapper: {
        css: wrapperStyles(),
    },
    /* Store Custom Code - Start */
    progressStepper: {
        extendedStyles: {
            wrapper: {
                css: css`
                    ${x => spacingStyles(null, x.theme.design.typography.spacing.base)}
                `,
            },
        },
    },
    titleWrapper: {
        css: css`
            display: flex;
            align-items: center;
            gap: ${pxToRem(8)};
        `,
    },
    titleIcon: {
        size: 32,
    },
    /* Store Custom Code - End */
};

/* Store Custom Code - Start */
export const orderHeaderStylesV2: OrderHeaderStyles = {
    wrapper: {
        css: css`
            ${spacingStyles(null, pxToRem(32))}
        `,
    },
    heading: {
        forwardAs: "h1",
        extendedStyles: {
            title: {
                css: css`
                    ${typographyTitleStyles(pxToRemArray([24, 30, null, 40]))}
                `,
            },
        },
    },
    messageWrapper: orderHeaderStyles.messageWrapper,
    zoneWrapper: orderHeaderStyles.zoneWrapper,
    progressStepper: {
        extendedStyles: {
            wrapper: {
                css: css`
                    ${spacingStyles(null, pxToRem(32))}
                    padding-bottom: ${pxToRem(24)};
                    border-bottom: 1px solid ${getColor("common.borderLight")};
                `,
            },
        },
    },
    titleWrapper: orderHeaderStyles.titleWrapper,
    titleIcon: orderHeaderStyles.titleIcon,
};
/* Store Custom Code - End */

const OrderHeader: FC<PropsWithChildren<OrderHeaderProps>> = ({
    children,
    extendedStyles,
    overridedStyles,
    title,
    contentId,
    message,
    linkBack,
    linkBackOnClick,
    image,
    link,
    // Store Custom Code
    pageIdSegment,
    progressSteps,
    progressActiveStep,
    isV2,
    titleIcon,
}) => {
    const styles = useExpandProps(isV2 ? orderHeaderStylesV2 : orderHeaderStyles, extendedStyles, overridedStyles); // Store Custom Code
    const zoneIsActive = useZoneIsActive(contentId || "", zoneName);

    return (
        <StyledWrapper {...styles.wrapper}>
            <LinkBack
                pageLink={linkBack}
                onClick={linkBackOnClick}
                {...styles.linkBack}
                // Store Custom Code
                pageIdSegment={pageIdSegment}
                isV2={isV2}
            />
            {/* Store Custom Code - Start */}
            {progressSteps && (
                <ProgressStepper {...styles.progressStepper} activeStep={progressActiveStep} steps={progressSteps} />
            )}
            {/* Store Custom Code - End */}
            {/* Store Custom Code - Start */}
            {titleIcon ? (
                <StyledWrapper {...styles.titleWrapper}>
                    <Icon {...styles.titleIcon} {...titleIcon} />
                    <Heading title={title} image={image} link={link} {...styles.heading}>
                        {children}
                    </Heading>
                </StyledWrapper>
            ) : (
                <Heading title={title} image={image} link={link} {...styles.heading}>
                    {children}
                </Heading>
            )}
            {/* Store Custom Code - End */}
            {message ? (
                <StyledWrapper {...styles.messageWrapper} className="message">
                    {message}
                </StyledWrapper>
            ) : null}
            {contentId && zoneIsActive ? (
                <StyledWrapper {...styles.zoneWrapper} className="message">
                    <Zone contentId={contentId} zoneName={zoneName} />
                </StyledWrapper>
            ) : null}
        </StyledWrapper>
    );
};

export default OrderHeader;
